import { memo, useRef, useState } from 'react'
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { FIELD_TYPES, FORMATS } from '../../../utility/constants'
import { getFieldId } from '../utils/utils'
import FieldHistory from '../../../components/DataEntry/FieldHistory'
import moment from 'moment'

const DateAndDateTime = ({
  id,
  field,
  value,
  onChange,
  type = FIELD_TYPES.DATETIME,
  className = '',
  label = null,
  placeholder = null,
  disabled = false,
  tableField = true,
  helperText = null,
  autoFocus = false,
  allValues,
  processContext,
  processId,
  sx = {
    '& .MuiInputBase-root': {
      height: 35,
      overflow: 'hidden',
    },
  },
  style = {},
  inputSx = {},
}) => {
  const inputRef = useRef(null)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const handleDateChange = (newValue) => {
    onChange(newValue)
  }

  const handleDatePickerOpen = () => {
    setIsDatePickerOpen(true)
  }

  const handleDatePickerClose = () => {
    setIsDatePickerOpen(false)
  }

  const handleAccept = (newValue) => {
    onChange(newValue)
    setIsDatePickerOpen(false)
  }

  const commonProps = {
    label,
    placeholder,
    autoFocus,
    value: value ? moment(value) : null,
    onChange: handleDateChange,
    onAccept: handleAccept, // This handles the OK button click
    onOpen: handleDatePickerOpen,
    onClose: handleDatePickerClose,
    disabled: disabled,
    open: isDatePickerOpen,
    openOnFocus: false, // Prevent opening on focus
    sx,
    slotProps: {
      textField: {
        inputRef,
        name: getFieldId(id),
        id: getFieldId(id),
        fullWidth: true,
        size: 'small',
        variant: 'outlined',
        className,
        color: 'primary',
        error: helperText != null,
        sx: { ...inputSx },

        // Remove onClick to allow manual typing
        // onClick: () => {
        //   if (!disabled) {
        //     setIsDatePickerOpen(true)
        //   }
        // },

        onFocus: (e) => {
          // Allow normal focus behavior without opening picker
          e.target.select && e.target.select()
        },
      },
    },
  }

  // Force consistent mobile view for both DatePicker and DateTimePicker
  commonProps.desktopModeMediaQuery = '@media (max-width: 0px)' // Forces mobile view always

  // For DateTime picker, add additional configuration
  if (type === FIELD_TYPES.DATETIME) {
    commonProps.timeViewsRenderers = undefined // This forces digital clock
  }

  if (!field || field.is_subfield) {
    commonProps['open'] = isDatePickerOpen
  }

  const pickerComponent =
    type === FIELD_TYPES.DATETIME ? (
      <DateTimePicker {...commonProps} format={FORMATS.DATETIME} />
    ) : (
      <DatePicker {...commonProps} format={FORMATS.DATE} />
    )

  const content = (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div style={{ width: '100%', ...style }}>
        <DemoContainer
          sx={{ width: '100%', overflow: 'visible', paddingTop: '0px' }}
          components={[type === FIELD_TYPES.DATETIME ? 'DateTimePicker' : 'DatePicker']}
        >
          {pickerComponent}
        </DemoContainer>
      </div>
    </LocalizationProvider>
  )

  if (!field || field.is_subfield) {
    return content
  } else {
    return !isDatePickerOpen ? (
      <div>
        <FieldHistory
          fieldId={field?.id}
          field={field}
          dynamicListData={processContext?.dynamicListData}
          processId={processId}
          allValues={allValues}
          helperText={helperText}
        >
          {content}
        </FieldHistory>
      </div>
    ) : (
      content
    )
  }
}

export default memo(
  DateAndDateTime,
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.disabled === nextProps.disabled,
)
